import barbadosMapData from '../../../data/map/barbados/map_data.json';
import puertoRicoMapData from '../../../data/map/puerto_rico/puerto_rico_map_data.json';
import detroitMapData from '../../../data/map/detroit_bankruptcy_solo/detroit_bankruptcy_solo_map_data.json';
import seattleMapData from '../../../data/map/seattle_metro/seattle_metro_map_data.json';
import type {
  Hex,
  City,
  Town,
  HexCoordinate,
  TerrainType,
  GoodsColor,
  Direction,
  TileType
} from '../../types';

/**
 * マップデータの型定義（汎用）
 * 新マップ作成時はこの形式でJSONを作成
 */
export interface MapData {
  map_info: {
    name: string;
    designer: string;
    players: number[];
    coordinate_system: string;
    origin: { q: number; r: number };
    orientation: string;
  };
  cities: Array<{
    name: string;
    color: string | string[];
    coordinates: { q: number; r: number };
    dice_color: string;
    dice_numbers: number[];
  }>;
  towns: Array<{
    name: string;
    coordinates: { q: number; r: number };
    urbanizable: boolean;
    terrain: string;
  }>;
  terrain: {
    mountains?: Array<{ q: number; r: number }>;
    rivers?: Array<{ q: number; r: number }>;
    deserts?: Array<{ q: number; r: number }>;
    plains?: Array<{ q: number; r: number }>;
  };
  // 特殊リンク（オプション）
  ferry_links?: Array<{
    from: { q: number; r: number };
    to: { q: number; r: number };
    cost: number;
  }>;
  adjacent_links?: Array<{
    from: { q: number; r: number };
    to: { q: number; r: number };
    cost: number;
  }>;
  // 壁（ヘクス間の通行不可境界）
  walls?: Array<{
    coordinates: { q: number; r: number };
    blocked_edges: number[];
  }>;
  // 敷設済み線路（ゲーム開始時に配置される所有者なし線路）
  pre_laid_tracks?: Array<{
    coordinates: { q: number; r: number };
    tile_type: string;
    rotation: number;
  }>;
}

/**
 * 汎用マップローダー
 * MapData形式のJSONデータからHex配列を生成
 *
 * @param data - マップデータ（JSON）
 * @returns Hex配列（マップ全体）
 */
export function loadMap(data: MapData): Hex[] {
  // 1. 全座標を収集
  const allCoordinates = new Set<string>();

  // 都市座標
  data.cities.forEach(city => {
    allCoordinates.add(`${city.coordinates.q},${city.coordinates.r}`);
  });

  // 町座標
  data.towns.forEach(town => {
    allCoordinates.add(`${town.coordinates.q},${town.coordinates.r}`);
  });

  // 地形座標
  Object.values(data.terrain).forEach(coords => {
    if (coords && Array.isArray(coords)) {
      coords.forEach(coord => {
        allCoordinates.add(`${coord.q},${coord.r}`);
      });
    }
  });

  // 2. Hex配列を構築
  const hexes: Hex[] = [];

  allCoordinates.forEach(coordStr => {
    const [q, r] = coordStr.split(',').map(Number);
    const coordinate: HexCoordinate = { q, r };

    // 地形判定
    const terrain = getTerrainType(coordinate, data.terrain);

    // 地形コスト
    const terrainCost = getTerrainCost(terrain);

    // 都市情報
    const cityData = data.cities.find(
      c => c.coordinates.q === q && c.coordinates.r === r
    );
    const city: City | null = cityData ? {
      id: cityData.name.replace(/\s+/g, '_').toUpperCase(),
      name: cityData.name,
      color: cityData.color as GoodsColor | GoodsColor[],
      coordinate,
      diceNumber: cityData.dice_numbers[0] || 0,
      diceColor: cityData.dice_color as 'white' | 'black',
      isNewCity: false,
      newCityId: null
    } : null;

    // 町情報
    const townData = data.towns.find(
      t => t.coordinates.q === q && t.coordinates.r === r
    );
    const town: Town | null = townData ? {
      id: townData.name.replace(/\s+/g, '_').toUpperCase(),
      coordinate,
      isUrbanized: false,
      newCity: null
    } : null;

    // 壁情報
    const wallData = data.walls?.find(
      w => w.coordinates.q === q && w.coordinates.r === r
    );
    const blockedEdges = wallData?.blocked_edges as Direction[] | undefined;

    // Hex生成
    const hex: Hex = {
      coordinate,
      terrain,
      terrainCost,
      tile: null,
      city,
      town,
      goods: []
    };

    // 壁がある場合のみ設定
    if (blockedEdges && blockedEdges.length > 0) {
      hex.blockedEdges = blockedEdges;
    }

    hexes.push(hex);
  });

  return hexes;
}

/**
 * Barbados マップデータを読み込み
 *
 * @returns Hex配列（マップ全体）
 */
export function loadBarbadosMap(): Hex[] {
  return loadMap(barbadosMapData as MapData);
}

/**
 * Puerto Rico マップデータを読み込み
 *
 * @returns Hex配列（マップ全体）
 */
export function loadPuertoRicoMap(): Hex[] {
  return loadMap(puertoRicoMapData as MapData);
}

/**
 * Detroit Bankruptcy Solo マップデータを読み込み
 *
 * @returns Hex配列（マップ全体）
 */
export function loadDetroitMap(): Hex[] {
  return loadMap(detroitMapData as MapData);
}

/**
 * Seattle Metro Solo マップデータを読み込み
 *
 * @returns Hex配列（マップ全体）
 */
export function loadSeattleMap(): Hex[] {
  return loadMap(seattleMapData as MapData);
}

/**
 * Seattle マップの敷設済み線路データを取得
 */
export function loadSeattlePreLaidTracks(): PreLaidTrack[] {
  return loadPreLaidTracks(seattleMapData as MapData);
}

/**
 * 特殊リンク（フェリー/隣接）の型定義
 */
export interface SpecialLink {
  id: string;
  from: { q: number; r: number };
  to: { q: number; r: number };
  cost: number;
  /** 所有プレイヤーID（未敷設の場合は null） */
  owner: string | null;
}

/**
 * マップの特殊リンク（フェリーリンク、隣接リンク）を取得
 */
export function loadMapLinks(data: MapData): {
  ferryLinks: SpecialLink[];
  adjacentLinks: SpecialLink[];
} {
  const ferryLinks: SpecialLink[] = (data.ferry_links || []).map((link, idx) => ({
    id: `ferry-${idx}`,
    from: link.from,
    to: link.to,
    cost: link.cost,
    owner: null,  // 初期状態は未所有
  }));

  const adjacentLinks: SpecialLink[] = (data.adjacent_links || []).map((link, idx) => ({
    id: `adjacent-${idx}`,
    from: link.from,
    to: link.to,
    cost: link.cost,
    owner: null,  // 初期状態は未所有
  }));

  return { ferryLinks, adjacentLinks };
}

/**
 * Detroit マップの特殊リンクを読み込み
 */
export function loadDetroitMapLinks(): {
  ferryLinks: SpecialLink[];
  adjacentLinks: SpecialLink[];
} {
  return loadMapLinks(detroitMapData as MapData);
}

/**
 * 敷設済み線路の型定義
 */
export interface PreLaidTrack {
  hexCoord: HexCoordinate;
  tileType: TileType;
  rotation: 0 | 1 | 2 | 3 | 4 | 5;
}

/**
 * マップの敷設済み線路データを取得
 */
export function loadPreLaidTracks(data: MapData): PreLaidTrack[] {
  if (!data.pre_laid_tracks) {
    return [];
  }

  return data.pre_laid_tracks.map(track => ({
    hexCoord: { q: track.coordinates.q, r: track.coordinates.r },
    tileType: track.tile_type as TileType,
    rotation: track.rotation as 0 | 1 | 2 | 3 | 4 | 5,
  }));
}

/**
 * 座標から地形タイプを判定
 */
function getTerrainType(
  coordinate: HexCoordinate,
  terrain: MapData['terrain']
): TerrainType {
  const coordStr = `${coordinate.q},${coordinate.r}`;

  if (terrain.mountains && terrain.mountains.some((c: { q: number; r: number }) => `${c.q},${c.r}` === coordStr)) {
    return 'mountains';
  }
  if (terrain.rivers && terrain.rivers.some((c: { q: number; r: number }) => `${c.q},${c.r}` === coordStr)) {
    return 'river';
  }
  // plains または都市/町
  return 'plains';
}

/**
 * 地形コストを取得
 */
function getTerrainCost(terrain: TerrainType): number {
  switch (terrain) {
    case 'plains':
      return 2;
    case 'mountains':
      return 4;
    case 'river':
      return 3;
    default:
      return 2;
  }
}
